import { Column, Container, Row } from '@/components/base/gridview'
import { path, useApp } from '@wap-client/core'
import React, { useState, useRef } from 'react'
import { mappers, ReferenceMapping } from '@wap-client/services'

import Panel from './Panel'
import { SearchProps } from './types'
import SearchFacilityList from './search-facility-list'
import { FacilityListSearchItem } from './search-facility-list/types'
import { GTMEvent } from '@/utils/gtm'
import { searchAllDomain } from './services'
import Anchor from '@/components/base/anchor'
import { Akiva } from '@/utils/akiva/akiva'
import { AkivaType } from '@/utils/akiva/types'
import useTranslations from '@/hooks/useTranslations'

const Search: React.FunctionComponent<SearchProps> = (props) => {
  const { onClose } = props

  const app = useApp()
  const { translation } = useTranslations()
  const [isLoading, setIsLoading] = useState(false)
  const [isSearched, setIsSearched] = useState(false)
  const [resultGroups, setResultGroups] = useState<any[]>([])
  const [hotels, setHotels] = useState<FacilityListSearchItem[]>([])

  const inputRef = useRef<HTMLInputElement>(null)

  const onInteract = (page: any) => {
    let event = {}

    event = {
      page: page,
    }

    GTMEvent('search-view', event, app)
    Akiva({
      app,
      type: AkivaType.GlobalSearch,
      data: {
        resultList: resultGroups,
        clickedResult: page || undefined,
        parameters: inputRef.current?.value,
      },
    })
  }

  const geturl = (domain: string, path: string) => {
    return (
      (domain.includes('http') ? '' : 'https://') +
      domain +
      '.goturkiye.com/' +
      path
    )
  }

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (!isSearched) {
        setIsSearched(true)
      }

      if (inputRef.current && inputRef.current.value.length > 2) {
        setIsLoading(true)

        searchAllDomain(app.environment, {
          language: app.language,
          search: inputRef.current?.value || '',
        })
          .then(async (response) => {
            try {
              const facilitiesResponse = await fetch(
                `https://api.goturkiye.com/core/goturkiye/green-certificate/v2/facilities?term=${inputRef.current?.value}&language=en&pageSize=20&pageIndex=0`,
              )

              const facilitiesData = await facilitiesResponse.json()

              if (facilitiesData?.attributes.list) {
                const mappings: ReferenceMapping[] = [
                  { name: 'title', path: 'brandName', type: 'static' },
                  { name: 'anchor', path: 'webSite', type: 'static' },
                  {
                    name: 'image',
                    path: 'hotelImageAttachment',
                    type: 'image',
                  },
                ]

                const list = mappers.getQuery(
                  facilitiesData?.attributes.list,
                  mappings,
                ) as FacilityListSearchItem[]

                setHotels(list)
              }
            } catch (err) {
              console.log('Fetch error:', err)
            }
            const results = groupDataByDomain(response)
            setResultGroups(results)
            setIsLoading(false)
          })
          .catch((err) => {
            console.log('Global search error:', err)
            setIsLoading(false)
          })
      }
    }
  }

  const groupDataByDomain = (data: any) => {
    const grouped: any = {}

    data.attributes.list.forEach((item: any) => {
      const domain = item.domain && item.domain.trim() ? item.domain : 'default'
      if (!grouped[domain]) {
        grouped[domain] = []
      }
      grouped[domain].push(item)
    })

    return Object.keys(grouped).map((domain) => ({
      domain,
      results: grouped[domain],
    }))
  }

  const RenderLoader = () => {
    return (
      <div className="search-loading">
        <div className="spinner">
          <div className="double-bounce1"></div>
          <div className="double-bounce2"></div>
        </div>
      </div>
    )
  }

  const RenderSearchResults = () => {
    if (resultGroups.length > 0 || hotels.length) {
      return (
        <div className="search-results scrollbar">
          <Row>
            {hotels.length ? (
              <Column xs={{ size: 12 }} style={{ width: '100%' }}>
                <div className="search-results-group">
                  <h5>{translation('sustainableHotels')}</h5>
                </div>
                <div className="search-results-group">
                  <SearchFacilityList
                    column={{
                      xs: { size: 6 },
                      sm: { size: 4 },
                      md: { size: 3 },
                      xl: { size: 2 },
                    }}
                    data={hotels}
                    isRotated={true}
                  />
                </div>
              </Column>
            ) : null}

            <Column xs={{ size: 12 }} xl={{ size: 6 }}>
              {resultGroups
                .slice(0, Math.ceil(resultGroups.length / 2))
                .map((groupItem, index) => {
                  return (
                    <div key={index} className="search-results-group">
                      <h5>
                        <Anchor href={geturl(groupItem.domain, '')}>
                          {groupItem.domain}.goturkiye.com
                        </Anchor>
                      </h5>
                      <ul>
                        {groupItem.results.map(
                          (resultItem: any, idx: number) => (
                            <li key={idx}>
                              <Anchor
                                onClick={() =>
                                  onInteract(
                                    geturl(groupItem.domain, resultItem.url),
                                  )
                                }
                                target={'_blank'}
                                href={geturl(groupItem.domain, resultItem.url)}
                              >
                                {resultItem.title}
                              </Anchor>
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )
                })}
            </Column>

            <Column xs={{ size: 12 }} xl={{ size: 6 }}>
              {resultGroups
                .slice(Math.ceil(resultGroups.length / 2), resultGroups.length)
                .map((groupItem, index) => {
                  return (
                    <div key={index} className="search-results-group">
                      <h5>
                        <Anchor href={geturl(groupItem.domain, '')}>
                          {groupItem.domain}.goturkiye.com
                        </Anchor>
                      </h5>
                      <ul>
                        {groupItem.results.map(
                          (resultItem: any, idx: number) => (
                            <li key={idx}>
                              <Anchor
                                onClick={() =>
                                  onInteract(
                                    geturl(groupItem.domain, resultItem.url),
                                  )
                                }
                                target={'_blank'}
                                href={geturl(groupItem.domain, resultItem.url)}
                              >
                                {resultItem.title}
                              </Anchor>
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )
                })}
            </Column>
          </Row>
        </div>
      )
    }

    if (isLoading) return null

    return (
      <p style={{ marginTop: 16, fontSize: 24 }}>{translation('noResult')}</p>
    )
  }

  return (
    <Panel onClose={onClose} isOpen={props.isOpen} logo={props.logo}>
      <Container size="extended">
        <div className="search">
          <div
            className={[
              'search-input-target',
              isSearched && 'search-input-target-active',
            ]
              .filter((item) => item)
              .join(' ')}
          >
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              className={['search-input', isSearched && 'search-input-active']
                .filter((item) => item)
                .join(' ')}
              onKeyUp={handleKeyUp}
              placeholder={translation('search')}
              ref={inputRef}
            />
          </div>
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {isLoading ? <RenderLoader /> : null}
            {isSearched ? <RenderSearchResults /> : null}
          </div>
        </div>
      </Container>
    </Panel>
  )
}

export default Search
